import {useState} from 'react';
import {useMutation} from '@apollo/client';
import {useSelector} from 'react-redux';
import {message} from 'antd';
import {ASSIGN_TICKET} from 'graphql/mutations/aiHelpdesk';
import {ResolveTicketModal} from './modals/ResolveTicketModal';

// Statuses an unassigned ticket can be pulled into a human's queue from.
// AI_ACTIVE (AI is handling it) and ESCALATED (flagged for a human) can be
// claimed; GUIDED_FLOW is still in the scripted intake flow and isn't claimable.
const ASSIGNABLE_STATUSES = ['AI_ACTIVE', 'ESCALATED'];

/**
 * Shared ticket quick-actions for the card/table dropdowns. Mirrors the
 * mutations wired in SingleTicketView, but scoped to what the list menus offer:
 *   • Assign to Me — pull an AI-active or escalated ticket into your own queue.
 *   • Mark Resolved — close a ticket, but ONLY one assigned to you. Opens the
 *     resolve dialog, which asks for the ticket's issue; render the returned
 *     `resolveModal` once beside the menu.
 *
 * `onChanged` should refresh the list (the layout's refetchTickets) so the row
 * reflects its new status/assignee after the mutation.
 */
export function useTicketActions(ticket, onChanged) {
  const admin = useSelector(state => state?.auth?.userData?.user);
  const adminId = admin?.id;
  const adminName = `${admin?.firstName || ''} ${admin?.lastName || ''}`.trim();

  const [assignMut, {loading: assigning}] = useMutation(ASSIGN_TICKET, {
    errorPolicy: 'all',
  });
  const [resolveOpen, setResolveOpen] = useState(false);
  const loading = assigning;

  const assignedAgentId = ticket?._raw?.assignedAgentId ?? null;
  // "Assigned to me" is the raw-id comparison (mapHelpdeskTicketToView collapses
  // it to the 'Current Agent' label, but we compare ids here to stay robust).
  const isAssignedToMe =
    !!assignedAgentId &&
    !!adminId &&
    String(assignedAgentId) === String(adminId);

  const canAssignToMe =
    !isAssignedToMe && ASSIGNABLE_STATUSES.includes(ticket?.status);
  // Only tickets assigned to you can be resolved from the list menu.
  const canResolve = isAssignedToMe && ticket?.status !== 'RESOLVED';

  const assignToMe = async () => {
    if (!adminId) {
      message.error('Could not identify the current agent.');
      return;
    }
    try {
      const res = await assignMut({
        variables: {
          input: {
            id: ticket.id,
            assignedAgentId: String(adminId),
            assignedAgentName: adminName || null,
            status: 'HUMAN_ACTIVE',
          },
        },
      });
      const node = res.data?.assignTicket;
      if (node?.__typename === 'Error') {
        message.error(node.message);
        return;
      }
      message.success('Ticket assigned to you');
      onChanged?.();
    } catch (err) {
      message.error(err.message);
    }
  };

  const resolve = () => {
    if (isAssignedToMe) setResolveOpen(true);
  };

  const resolveModal = canResolve ? (
    <ResolveTicketModal
      ticket={{...ticket?._raw, id: ticket?.id}}
      isOpen={resolveOpen}
      onClose={() => setResolveOpen(false)}
      onResolved={onChanged}
    />
  ) : null;

  return {
    isAssignedToMe,
    canAssignToMe,
    canResolve,
    assignToMe,
    resolve,
    resolveModal,
    loading,
  };
}
